import { useEffect, useState } from 'react';
import cn from 'classnames';

import {
  isNiceChatEnabled,
  loadNiceChat,
  openNiceChat
} from 'Services/niceChat';

import pdiLogoWhite from 'Assets/pdi_logo_white.svg';

import './style.scss';

// Quick links shown in the panel before a chat starts ({ title, subtitle, url }),
// from TTEC's skupos-chat-custom-entry-basic snippet (Oct 8). The card hides when empty.
const SUPPORT_LINKS = [
  {
    title: 'Skupos Help Center',
    url: 'https://help.pditechnologies.com/LegacySkupos/#/home/104583/10/11'
  },
  { title: 'Beyond Trust', url: 'https://connect.skupos.com' },
  {
    title: 'VSA X Remote Support',
    url:
      'https://pdisoftware.vsax.net/installer?type=windows_agent_x64&secret=acd6b30512e249f3bab6a0c975c869ad'
  },
  { title: 'Show my PC', url: 'https://pdi.showmypc.com' }
];

const HOURS = {
  timeZone: 'America/Chicago',
  days: [1, 2, 3, 4, 5],
  open: 8,
  close: 17
};

export function isSupportOpen(now = new Date()) {
  const parts = {};
  new Intl.DateTimeFormat('en-US', {
    timeZone: HOURS.timeZone,
    weekday: 'short',
    hour: 'numeric',
    hour12: false
  })
    .formatToParts(now)
    .forEach(p => {
      parts[p.type] = p.value;
    });
  const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(
    parts.weekday
  );
  const hour = parseInt(parts.hour, 10) % 24;
  return HOURS.days.includes(day) && hour >= HOURS.open && hour < HOURS.close;
}

// CXone's chat window (#be-frame) carries be-frame-default while open and
// be-frame-hidden once minimized from its header.
function isChatWindowOpen() {
  const frame = document.getElementById('be-frame');
  return !!frame && frame.classList.contains('be-frame-default');
}

// While the chat window is open our launcher hides: it would otherwise sit on
// top of the window's bottom edge, and clicking it reopened our panel beside
// the chat. Minimizing from the chat header brings the launcher back.
function useChatWindowOpen(enabled) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const sync = () => setOpen(isChatWindowOpen());
    const observer = new MutationObserver(sync);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
    sync();
    return () => observer.disconnect();
  }, [enabled]);

  return open;
}

export default function NiceChat() {
  const enabled = isNiceChatEnabled();
  const [isOpen, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  const chatOpen = useChatWindowOpen(enabled);

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    loadNiceChat().then(ok => {
      if (active && !ok) setFailed(true);
    });
    return () => {
      active = false;
    };
  }, [enabled]);

  // The chat may also open from elsewhere (e.g. "Get in touch").
  useEffect(() => {
    if (chatOpen) setOpen(false);
  }, [chatOpen]);

  // Script failed: no bubble, the rest of the dashboard carries on.
  if (!enabled || failed || chatOpen) return null;

  const supportOpen = isSupportOpen();

  const startChat = () => {
    setOpen(false);
    openNiceChat();
  };

  return (
    <div className="NiceChat">
      <section
        className={cn('NiceChat--panel', { 'is-open': isOpen })}
        aria-label="Support"
        aria-hidden={!isOpen}
      >
        <div className="NiceChat--scroll">
          <div className="NiceChat--header">
            <div className="NiceChat--top">
              <div className="NiceChat--logo">
                <img src={pdiLogoWhite} alt="PDI Technologies" />
              </div>
              <button
                type="button"
                className="NiceChat--close"
                aria-label="Close"
                tabIndex={isOpen ? 0 : -1}
                onClick={() => setOpen(false)}
              >
                &times;
              </button>
            </div>
            <p className="NiceChat--greeting">
              Hi there! 👋
              <br />
              How can we help you?
            </p>
          </div>

          <div className="NiceChat--body">
            <button
              type="button"
              className="NiceChat--card NiceChat--message"
              tabIndex={isOpen ? 0 : -1}
              onClick={startChat}
            >
              <div>
                <strong>Send us a message</strong>
                <span className="NiceChat--sub">
                  <span
                    className={cn('NiceChat--dot', { 'is-on': supportOpen })}
                  />
                  <span>
                    {supportOpen
                      ? 'We typically reply in a few minutes'
                      : "We're offline right now. Leave a message and we'll reply."}
                  </span>
                </span>
              </div>
              <svg
                className="NiceChat--arrow"
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M3 20.5 21 12 3 3.5l2.5 8.5L3 20.5Z" />
              </svg>
            </button>

            {SUPPORT_LINKS.length > 0 && (
              <nav
                className="NiceChat--card NiceChat--links"
                aria-label="Quick links"
              >
                {SUPPORT_LINKS.map(link => (
                  <a
                    key={link.url}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    tabIndex={isOpen ? 0 : -1}
                  >
                    <span>
                      <span className="NiceChat--link-title">{link.title}</span>
                      {link.subtitle && (
                        <span className="NiceChat--link-sub">
                          {link.subtitle}
                        </span>
                      )}
                    </span>
                    <svg
                      className="NiceChat--ext"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <path d="M15 3h6v6" />
                      <path d="M10 14 21 3" />
                    </svg>
                  </a>
                ))}
              </nav>
            )}
          </div>
        </div>
      </section>

      <button
        type="button"
        className={cn('NiceChat--launcher', { 'is-open': isOpen })}
        aria-label={isOpen ? 'Close support' : 'Open support'}
        aria-expanded={isOpen}
        onClick={() => setOpen(open => !open)}
      >
        <svg
          className="NiceChat--icon NiceChat--icon-open"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        <svg
          className="NiceChat--icon NiceChat--icon-close"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
        >
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
